// Stateless action helpers for watchlists. These functions do not
// capture React state; instead they receive all dependencies explicitly.

import analyticsService from '../services/analyticsService';
import { isBaseFuturesProduct, getCurrentContractSymbol } from '../utils/futuresContracts';

/** Update every user-scoped watchlistTickers entry for this watchlist id. */
const setWatchlistTickersCache = (queryClient, watchlistId, data) => {
  if (!queryClient || watchlistId == null) return;
  queryClient.setQueriesData(
    {
      predicate: (query) =>
        query.queryKey[0] === 'watchlistTickers' &&
        query.queryKey[1]?.watchlistId === watchlistId,
    },
    data
  );
};

export const loadWatchlistsAction = async (api, setWatchlists, setSelectedWatchlistId, setLoading, setError) => {
  setLoading?.(true);
  setError?.(null);
  try {
    const res = await api.listWatchlists();
    const lists = res?.watchlists || [];
    setWatchlists(lists);
    setSelectedWatchlistId((prev) => prev ?? (lists[0]?.id || null));
  } catch (e) {
    setError?.(e?.message || 'Failed to load watchlists');
  } finally {
    setLoading?.(false);
  }
};

export const loadTickersAction = async (api, watchlistId, setTickers) => {
  if (!watchlistId) return;
  const res = await api.listTickers(watchlistId);
  setTickers(res?.tickers || []);
};

/**
 * @param {object} [options]
 * @param {string[]} [options.initialTickers] - Symbols to add after create (bootstrap only).
 * @param {boolean} [options.selectAfterCreate=true] - Select the new list when done.
 */
export const createWatchlistAction = async (
  api,
  name,
  loadWatchlists,
  setSelectedWatchlistId,
  options = {}
) => {
  const { initialTickers = [], selectAfterCreate = true } = options;
  const res = await api.createWatchlist(name);

  // Track watchlist created
  if (res?.watchlist?.id) {
    analyticsService.trackTradingEvent('watchlist_created', {
      watchlistName: name,
    });

    for (const ticker of initialTickers) {
      try {
        await api.addTicker(res.watchlist.id, ticker);
      } catch (err) {
        console.warn('[createWatchlistAction] Failed to add default ticker:', ticker, err);
      }
    }
  }

  await loadWatchlists();
  if (selectAfterCreate && res?.watchlist?.id) {
    setSelectedWatchlistId(res.watchlist.id);
  }
  return res;
};

export const renameWatchlistAction = async (api, id, name, loadWatchlists) => {
  const res = await api.renameWatchlist(id, name);
  await loadWatchlists();
  return res;
};

export const deleteWatchlistAction = async (api, id, selectedWatchlistId, setSelectedWatchlistId, setTickers, loadWatchlists) => {
  await api.deleteWatchlist(id);
  await loadWatchlists();
  if (selectedWatchlistId === id) {
    setSelectedWatchlistId(null);
    setTickers([]);
  }
};

export const addTickerAction = async (api, selectedWatchlistId, ticker, currentTickers, setTickers, _loadTickers, queryClient) => {
  if (!selectedWatchlistId) return;
  let upper = ticker.toUpperCase();
  
  // Check if this is a base futures product and calculate current contract
  let actualSymbol = upper;
  if (isBaseFuturesProduct(upper)) {
    actualSymbol = getCurrentContractSymbol(upper);
    console.log(`🔄 [addTickerAction] Auto-calculating futures contract: ${upper} → ${actualSymbol}`);
  }
  
  const optimistic = { ticker: actualSymbol, position: (currentTickers?.length || 0) + 1 };
  // Optimistic add if not already present
  setTickers((prev) => (prev.some(t => t.ticker === actualSymbol) ? prev : [...prev, optimistic]));
  try {
    const res = await api.addTicker(selectedWatchlistId, actualSymbol);
    const serverItem = res?.ticker || { ticker: actualSymbol, position: optimistic.position };

    // Track watchlist ticker added
    analyticsService.trackTradingEvent('watchlist_ticker_added', {
      ticker: actualSymbol,
      watchlistId: selectedWatchlistId,
      originalInput: upper !== actualSymbol ? upper : undefined
    });

    // Reconcile with server result without hard refetch
    setTickers((prev) => {
      const withoutDup = prev.filter(t => t.ticker !== serverItem.ticker);
      const updated = [...withoutDup, serverItem].sort((a,b) => (a.position||0) - (b.position||0));
      
      setWatchlistTickersCache(queryClient, selectedWatchlistId, updated);
      
      return updated;
    });

    return res;
  } catch (e) {
    // Rollback optimistic add on error
    setTickers((prev) => prev.filter(t => t.ticker === actualSymbol));
    // Rollback React Query cache too
    setWatchlistTickersCache(queryClient, selectedWatchlistId, currentTickers);
    throw e;
  }
};

export const removeTickerAction = async (api, selectedWatchlistId, ticker, prevTickers, setTickers, queryClient) => {
  if (!selectedWatchlistId) return;
  const upper = ticker.toUpperCase();
  const newTickers = prevTickers.filter(t => t.ticker !== upper);
  setTickers(newTickers);
  try {
    await api.removeTicker(selectedWatchlistId, upper);
    
    setWatchlistTickersCache(queryClient, selectedWatchlistId, newTickers);
  } catch (e) {
    setTickers(prevTickers); // rollback
    setWatchlistTickersCache(queryClient, selectedWatchlistId, prevTickers);
    throw e;
  }
};

export const reorderTickersAction = async (api, selectedWatchlistId, newOrder, prevTickers, setTickers, queryClient) => {
  if (!selectedWatchlistId) return;
  const items = newOrder.map((t, idx) => ({ ticker: t, position: idx + 1 }));
  const newTickers = items.map(({ ticker, position }) => ({ ticker, position }));
  setTickers(newTickers);
  try {
    await api.reorderTickers(selectedWatchlistId, items);
    
    setWatchlistTickersCache(queryClient, selectedWatchlistId, newTickers);
  } catch (e) {
    setTickers(prevTickers); // rollback
    setWatchlistTickersCache(queryClient, selectedWatchlistId, prevTickers);
    throw e;
  }
};

